import type {
    Condition,
    OptionValue,
    MetadataField,
    FieldType,
    NewRiskDirectory,
    RiskDirectoryResourceDto,
    CreateRiskDirectory,
    RiskDirectoryNodeView,
    StandardNode,
    ProcessScope,
    RiskScope,
    SingleRiskDirectoryNodeView,
    UpdatedRiskDirectoryNode,
    UpdatedScope,
    NewRiskDirectoryNode,
    NewScope,
} from "api/generated/model";

type NodeType = SingleRiskDirectoryNodeView["type"];
type NodeTypeWithMetadata = StandardNode["type"];

type TextInputField = Extract<FieldType, { type: "TextInput" }>;
type NumericInputField = Extract<FieldType, { type: "NumericInput" }>;
type TextAreaField = Extract<FieldType, { type: "TextArea" }>;
type DropdownField = Extract<FieldType, { type: "Dropdown" }>;
type RadioButtonGroupField = Extract<FieldType, { type: "RadioButtonGroup" }>;
type CheckboxGroupField = Extract<FieldType, { type: "CheckboxGroup" }>;
type HeadingField = Extract<FieldType, { type: "Heading" }>;
type AttachedFilesField = Extract<FieldType, { type: "AttachedFiles" }>;

type RiskDirectoryId = number;

interface RiskDirectory {
    id: number;
    title: string;
    responsibleId?: number;
}

type RiskDirectoryNode = RiskDirectoryNodeView;

type ParentNode = RiskDirectoryNode | ProcessNode;

type ProcessNode = Extract<StandardNode, { type: "Process" }>;

type RiskNode = Extract<StandardNode, { type: "Risk" }>;

type SingleRiskNodeView = Extract<
    SingleRiskDirectoryNodeView,
    { type: "Risk" }
>;
type SingleProcessNodeView = Extract<
    SingleRiskDirectoryNodeView,
    { type: "Process" }
>;

type SingleStandardNodeView = SingleProcessNodeView | SingleRiskNodeView;

const Response = {
    NotApplicable: 0 as const,
    Avoid: 1 as const,
    Transfer: 2 as const,
    Reduce: 3 as const,
    Accept: 4 as const,
};

type ResponseEnum =
    | typeof Response.NotApplicable
    | typeof Response.Avoid
    | typeof Response.Transfer
    | typeof Response.Reduce
    | typeof Response.Accept;

/*
 * Update node
 */

type UpdatedGlobalScope = Extract<UpdatedScope, { type: "UpdatedGlobal" }>;
type UpdatedGlobalScopeWithContent = Extract<
    UpdatedScope,
    { type: "UpdatedGlobalWithContent" }
>;
type UpdatedLocalScopeWithContent = Extract<
    UpdatedScope,
    { type: "UpdatedLocalWithContent" }
>;

type UpdatedRiskScope =
    | UpdatedGlobalScopeWithContent
    | UpdatedLocalScopeWithContent;

/*
 * Create node
 */

type NewRiskDirectoryNodeProcess = Extract<
    NewRiskDirectoryNode,
    { type: "Process" }
>;
type NewRiskDirectoryNodeRisk = Extract<NewRiskDirectoryNode, { type: "Risk" }>;

type NewGlobalScope = Extract<NewScope, { type: "NewGlobal" }>;
type NewLocalScope = Extract<NewScope, { type: "NewLocal" }>;

const RISK_EXTRACTOR_KEYS = {
    PATH: "path",
    NAME: "name",
    DESCRIPTION: "description",
    CONTROLS: "controls",
    CONTROL_PROGRAMS: "controlPrograms",
    ENTITIES: "entities",
    EXPANDED_RISK_NODE_ENTITY: "expandedRiskNodeEntity",
    RISK: "risk",
    PROCESSES: "processes",
    SCOPE: "scope",
    FILTER_SCOPE: "filterScope",
    ASSESSMENT_STATUS: "assessmentStatus",
    ASSESSMENT_USER: "assessmentUser",
    ASSESSMENT_DATE: "assessmentDate",
    INHERENT_FOCUS: "inherentFocus",
    RESIDUAL_FOCUS: "residualFocus",
} as const;

const ENTITY_EXTRACTOR_KEYS = {
    ENTITY_NAME: "entityName",
} as const;

export type {
    CreateRiskDirectory,
    NewRiskDirectory,
    RiskDirectoryId,
    RiskDirectory,
    TextInputField,
    NumericInputField,
    TextAreaField,
    DropdownField,
    RadioButtonGroupField,
    CheckboxGroupField,
    HeadingField,
    AttachedFilesField,
    RiskDirectoryResourceDto as RiskDirectoryView,
    MetadataField,
    FieldType,
    RiskDirectoryNode,
    SingleRiskNodeView,
    SingleProcessNodeView,
    SingleStandardNodeView,
    StandardNode,
    ParentNode,
    ProcessNode,
    RiskNode,
    ProcessScope,
    RiskScope,
    NewScope,
    NewGlobalScope,
    NewLocalScope,
    OptionValue,
    NodeType,
    NodeTypeWithMetadata,
    ResponseEnum,
    Condition,
    // Update node
    UpdatedRiskDirectoryNode,
    UpdatedScope,
    UpdatedRiskScope,
    UpdatedGlobalScope,
    UpdatedGlobalScopeWithContent,
    UpdatedLocalScopeWithContent,
    // Create node
    NewRiskDirectoryNode,
    NewRiskDirectoryNodeProcess,
    NewRiskDirectoryNodeRisk,
};
export { Response, RISK_EXTRACTOR_KEYS, ENTITY_EXTRACTOR_KEYS };
